import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  FaBars, FaTimes, FaGraduationCap, FaSun, FaMoon,
  FaChevronDown, FaSignOutAlt, FaThLarge, FaUser,
} from 'react-icons/fa';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Courses', to: '/courses' },
  { label: 'Internship', to: '/internship' },
  { label: 'PYQ Papers', to: '/pyq' },
  { label: 'Projects', to: '/projects' },
  { label: 'Services', to: '/services' },
  { label: 'Blog', to: '/blog' },
  { label: 'About', to: '/about' },
];

function Header() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  const displayName = user ? (user.username || user.name || 'Student') : '';
  const initial = displayName ? displayName.charAt(0).toUpperCase() : 'U';

  const getDashboardRoute = () => {
    if (!user) return '/dashboard';
    const role = user.role ? user.role.toLowerCase() : '';
    if (role === 'admin') return '/admin-dashboard';
    if (role === 'instructor') return '/instructor/courses';
    return '/dashboard';
  };

  // Stronger elevation once the page scrolls under the bar.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the user dropdown on outside click / route change.
  useEffect(() => {
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  return (
    <header
      className="bg-header glass"
      style={{ ...styles.header, boxShadow: scrolled ? 'var(--elev-2)' : 'none' }}
    >
      <style>{css}</style>
      <div style={styles.container} className="bg-header-inner">
        {/* Logo */}
        <Link to="/" style={styles.logoLink} aria-label="ByteGurukul home">
          <span style={styles.logoMark} aria-hidden="true"><FaGraduationCap /></span>
          <span style={styles.logoText} className="gradient-text">ByteGurukul</span>
        </Link>

        {/* Desktop nav */}
        <nav style={styles.nav} className="bg-desktop-nav" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="bg-nav-link"
              style={{ ...styles.navLink, ...(isActive(l.to) ? styles.navLinkActive : {}) }}
            >
              {l.label}
            </Link>
          ))}
          {user && (
            <Link
              to={getDashboardRoute()}
              className="bg-nav-link"
              style={{ ...styles.navLink, ...(isActive(getDashboardRoute()) ? styles.navLinkActive : {}) }}
            >
              Dashboard
            </Link>
          )}
        </nav>

        {/* Desktop controls */}
        <div style={styles.controls} className="bg-desktop-controls">
          <button
            onClick={toggleTheme}
            style={styles.iconBtn}
            className="bg-icon-btn"
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label="Toggle theme"
          >
            {isDarkMode ? <FaSun /> : <FaMoon />}
          </button>

          {user ? (
            <div style={{ position: 'relative' }} ref={userMenuRef}>
              <button
                style={styles.userChip}
                className="bg-user-chip"
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <span style={styles.avatar} aria-hidden="true">{initial}</span>
                <span style={styles.userName}>{displayName}</span>
                <FaChevronDown style={{ fontSize: 11, opacity: 0.7, transform: userMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
              </button>
              {userMenuOpen && (
                <div style={styles.menu} role="menu" className="bg-fade-in">
                  <Link to={getDashboardRoute()} style={styles.menuItem} className="bg-menu-item" role="menuitem">
                    <FaThLarge style={styles.menuIcon} /> Dashboard
                  </Link>
                  <Link to="/student/profile" style={styles.menuItem} className="bg-menu-item" role="menuitem">
                    <FaUser style={styles.menuIcon} /> Profile
                  </Link>
                  <button onClick={logout} style={{ ...styles.menuItem, ...styles.menuLogout }} className="bg-menu-item" role="menuitem">
                    <FaSignOutAlt style={styles.menuIcon} /> Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="btn-ui btn-ghost-ui" style={styles.compactBtn}>Login</Link>
              <Link to="/signup" className="btn-ui btn-primary-ui" style={styles.compactBtn}>Sign Up</Link>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button style={styles.hamburger} className="bg-hamburger" onClick={() => setMobileOpen((v) => !v)} aria-label="Menu" aria-expanded={mobileOpen}>
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={styles.mobileMenu} className="bg-surface-panel bg-fade-in">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} style={{ ...styles.mobileLink, ...(isActive(l.to) ? styles.mobileLinkActive : {}) }}>{l.label}</Link>
          ))}
          {user && <Link to={getDashboardRoute()} style={styles.mobileLink}>Dashboard</Link>}
          <div style={styles.mobileControls}>
            <button onClick={toggleTheme} style={styles.mobileThemeBtn}>
              {isDarkMode ? <><FaSun /> Light mode</> : <><FaMoon /> Dark mode</>}
            </button>
            {user ? (
              <button onClick={logout} className="btn-ui" style={{ ...styles.mobileLogout }}>
                <FaSignOutAlt /> Logout ({displayName})
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <Link to="/login" className="btn-ui btn-outline-ui" style={{ flex: 1 }}>Login</Link>
                <Link to="/signup" className="btn-ui btn-primary-ui" style={{ flex: 1 }}>Sign Up</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

const styles = {
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    width: '100%',
    borderBottom: '1px solid var(--header-border)',
    transition: 'box-shadow .25s ease',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    width: '100%',
    maxWidth: 1280,
    height: 64,
    margin: '0 auto',
    padding: '0 20px',
    boxSizing: 'border-box',
  },
  logoLink: { display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 },
  logoMark: {
    display: 'grid', placeItems: 'center', width: 36, height: 36,
    borderRadius: 11, color: '#fff', fontSize: 18,
    background: 'var(--grad-brand)', boxShadow: 'var(--elev-brand)',
  },
  logoText: { fontFamily: 'var(--font-display)', fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em' },

  nav: { display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'center' },
  navLink: {
    textDecoration: 'none', color: 'var(--text-secondary)', fontWeight: 600, fontSize: 14.5,
    padding: '8px 13px', borderRadius: 9, whiteSpace: 'nowrap', transition: 'all .18s ease',
  },
  navLinkActive: { color: 'var(--brand)', background: 'var(--brand-soft)' },

  controls: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 },
  iconBtn: {
    display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: 10,
    border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-primary)',
    cursor: 'pointer', fontSize: 15,
  },
  compactBtn: { padding: '9px 16px', fontSize: 14 },

  userChip: {
    display: 'inline-flex', alignItems: 'center', gap: 9, padding: '5px 12px 5px 5px',
    border: '1px solid var(--border)', borderRadius: 999, background: 'transparent',
    color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600, fontSize: 14,
  },
  avatar: {
    display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: '50%',
    background: 'var(--grad-brand)', color: '#fff', fontSize: 13, fontWeight: 700,
  },
  userName: { maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  menu: {
    position: 'absolute', top: 'calc(100% + 10px)', right: 0, minWidth: 190,
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
    boxShadow: 'var(--elev-3)', padding: 6, display: 'flex', flexDirection: 'column', gap: 2,
  },
  menuItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 9,
    color: 'var(--text-primary)', textDecoration: 'none', fontSize: 14, fontWeight: 500,
    background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left',
  },
  menuLogout: { color: 'var(--error)' },
  menuIcon: { fontSize: 13, opacity: 0.85 },

  hamburger: {
    display: 'none', background: 'transparent', border: '1px solid var(--border)',
    borderRadius: 10, width: 40, height: 40, fontSize: 18, color: 'var(--text-primary)',
    cursor: 'pointer', placeItems: 'center',
  },

  mobileMenu: {
    display: 'flex', flexDirection: 'column', gap: 6, padding: 16,
    borderTop: '1px solid var(--border)',
  },
  mobileLink: {
    textDecoration: 'none', color: 'var(--text-primary)', fontSize: 15, fontWeight: 600,
    padding: '12px 14px', borderRadius: 10,
  },
  mobileLinkActive: { color: 'var(--brand)', background: 'var(--brand-soft)' },
  mobileControls: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 },
  mobileThemeBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '11px', border: '1px solid var(--border)', borderRadius: 10, background: 'transparent',
    color: 'var(--text-primary)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
  },
  mobileLogout: { background: 'var(--error)', color: '#fff', width: '100%' },
};

const css = `
  .bg-header { background: var(--header-bg); }
  .bg-surface-panel { background: var(--surface); }
  .bg-fade-in { animation: bgHeaderFade .18s ease both; }
  @keyframes bgHeaderFade { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }

  @media (hover: hover) {
    .bg-nav-link:hover { color: var(--brand); background: var(--brand-soft); }
    .bg-icon-btn:hover { border-color: var(--brand); color: var(--brand); }
    .bg-user-chip:hover { border-color: var(--brand); }
    .bg-menu-item:hover { background: var(--brand-soft); }
  }

  @media (max-width: 992px) {
    .bg-desktop-nav, .bg-desktop-controls { display: none !important; }
    .bg-hamburger { display: grid !important; }
  }
`;

export default Header;
